import { BaseNodeCommand, NodeChangeRecord } from './base/BaseNodeCommand';
import { useDiagramStore } from '../../stores/diagramStore';
import { computeDockedLayoutUpdatesForTable } from '../../domains/diagram/shapes/table/tableDockLayout.utils';
import { SessionMetrics, TABLE_SESSION_METRICS } from '../../domains/analytics';

const MIN_CELL = 20;

type Axis = 'row' | 'column';
type Op = 'insert' | 'remove';
type Position = 'before' | 'after';

export interface ModifyTableGridAtIndexParams {
  axis: Axis;
  op: Op;
  position?: Position; // only for insert
  // Render-space indices (rows include header if visible; cols are 0..columnCount-1)
  rowIndexByNodeId?: Record<string, number>; // required when axis==='row'
  colIndexByNodeId?: Record<string, number>; // required when axis==='column'
}

const clampCount = (value: unknown, fallback: number, min = 1): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(min, Math.floor(value));
  }
  return fallback;
};

function ensureArray(arr: unknown, count: number, fallbackTotal: number): number[] {
  if (Array.isArray(arr) && arr.length === count && arr.every((n) => typeof n === 'number' && Number.isFinite(n) && n > 0)) {
    return arr as number[];
  }
  const even = Math.max(MIN_CELL, fallbackTotal / Math.max(1, count));
  return Array(count).fill(even);
}

// Helpers to transform cell maps keyed by `${row},${col}` where `row` is render-space (includes header index 0 when visible)
function shiftCells(
  map: Record<string, any>,
  axis: Axis,
  fromIndex: number,
  delta: 1 | -1
): Record<string, any> {
  const out: Record<string, any> = {};
  const entries = Object.entries(map);
  for (const [k, v] of entries) {
    const [rStr, cStr] = k.split(',');
    let r = Number(rStr);
    let c = Number(cStr);
    if (!Number.isFinite(r) || !Number.isFinite(c)) continue;
    if (axis === 'row') {
      if (r >= fromIndex) r += delta;
    } else {
      if (c >= fromIndex) c += delta;
    }
    out[`${r},${c}`] = v;
  }
  return out;
}

function removeCellsAtIndex(
  map: Record<string, any>,
  axis: Axis,
  index: number
): Record<string, any> {
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(map)) {
    const [rStr, cStr] = k.split(',');
    const r = Number(rStr);
    const c = Number(cStr);
    if (!Number.isFinite(r) || !Number.isFinite(c)) continue;
    if (axis === 'row') {
      if (r === index) continue; // drop
      const nr = r > index ? r - 1 : r;
      out[`${nr},${c}`] = v;
    } else {
      if (c === index) continue; // drop
      const nc = c > index ? c - 1 : c;
      out[`${r},${nc}`] = v;
    }
  }
  return out;
}

function adjustMergedRegionsOnInsert(
  regions: unknown,
  axis: Axis,
  index: number
): Array<{ startRow: number; startCol: number; endRow: number; endCol: number }> {
  if (!Array.isArray(regions)) return [];
  const out: Array<{ startRow: number; startCol: number; endRow: number; endCol: number }> = [];
  for (const r of regions) {
    if (!r || typeof r !== 'object') continue;
    const mr = r as any;
    const startRow = Number(mr.startRow);
    const endRow = Number(mr.endRow);
    const startCol = Number(mr.startCol);
    const endCol = Number(mr.endCol);
    if (![startRow, endRow, startCol, endCol].every((n) => Number.isFinite(n))) continue;
    if (axis === 'row') {
      const nextStart = startRow >= index ? startRow + 1 : startRow;
      const nextEnd = endRow >= index ? endRow + 1 : endRow;
      out.push({ startRow: nextStart, endRow: nextEnd, startCol, endCol });
    } else {
      const nextStart = startCol >= index ? startCol + 1 : startCol;
      const nextEnd = endCol >= index ? endCol + 1 : endCol;
      out.push({ startRow, endRow, startCol: nextStart, endCol: nextEnd });
    }
  }
  return out;
}

function adjustMergedRegionsOnRemove(
  regions: unknown,
  axis: Axis,
  index: number
): Array<{ startRow: number; startCol: number; endRow: number; endCol: number }> {
  if (!Array.isArray(regions)) return [];
  const out: Array<{ startRow: number; startCol: number; endRow: number; endCol: number }> = [];
  for (const r of regions) {
    if (!r || typeof r !== 'object') continue;
    const mr = r as any;
    const startRow = Number(mr.startRow);
    let endRow = Number(mr.endRow);
    const startCol = Number(mr.startCol);
    let endCol = Number(mr.endCol);
    if (![startRow, endRow, startCol, endCol].every((n) => Number.isFinite(n))) continue;

    if (axis === 'row') {
      if (endRow < index) {
        out.push({ startRow, endRow, startCol, endCol });
        continue;
      }
      if (startRow > index) {
        out.push({ startRow: startRow - 1, endRow: endRow - 1, startCol, endCol });
        continue;
      }
      if (startRow <= index && endRow >= index) {
        endRow -= 1;
        if (endRow >= startRow) {
          const isSingleCell = startRow === endRow && startCol === endCol;
          if (!isSingleCell) out.push({ startRow, endRow, startCol, endCol });
        }
        continue;
      }
    } else {
      if (endCol < index) {
        out.push({ startRow, endRow, startCol, endCol });
        continue;
      }
      if (startCol > index) {
        out.push({ startRow, endRow, startCol: startCol - 1, endCol: endCol - 1 });
        continue;
      }
      if (startCol <= index && endCol >= index) {
        endCol -= 1;
        if (endCol >= startCol) {
          const isSingleCell = startRow === endRow && startCol === endCol;
          if (!isSingleCell) out.push({ startRow, endRow, startCol, endCol });
        }
        continue;
      }
    }
  }
  return out;
}

const parseCellKey = (value: unknown): { r: number; c: number } | null => {
  if (typeof value !== 'string') return null;
  const [rStr, cStr] = value.split(',');
  const r = Number(rStr);
  const c = Number(cStr);
  if (!Number.isFinite(r) || !Number.isFinite(c)) return null;
  return { r, c };
};

const makeCellKey = (r: number, c: number): `${number},${number}` => `${r},${c}` as `${number},${number}`;

// Copy styles from a neighbor row/column to the newly inserted row/column
function inheritStylesFromNeighbor(
  styles: Record<string, unknown>,
  axis: Axis,
  insertIndex: number,
  neighborIndex: number,
  totalRows: number,
  totalCols: number
): Record<string, unknown> {
  const result = { ...styles };
  
  if (axis === 'row') {
    // Copy styles from neighbor row to all cells in the new row
    for (let c = 0; c < totalCols; c += 1) {
      const neighborKey = makeCellKey(neighborIndex, c);
      const newKey = makeCellKey(insertIndex, c);
      const neighborStyle = styles[neighborKey];
      if (neighborStyle && typeof neighborStyle === 'object') {
        result[newKey] = JSON.parse(JSON.stringify(neighborStyle));
      }
    }
  } else {
    // Copy styles from neighbor column to all cells in the new column
    for (let r = 0; r < totalRows; r += 1) {
      const neighborKey = makeCellKey(r, neighborIndex);
      const newKey = makeCellKey(r, insertIndex);
      const neighborStyle = styles[neighborKey];
      if (neighborStyle && typeof neighborStyle === 'object') {
        result[newKey] = JSON.parse(JSON.stringify(neighborStyle));
      }
    }
  }
  
  return result;
}

export class ModifyTableGridAtIndexCommand extends BaseNodeCommand {
  private deletedDockedNodes: Array<Record<string, unknown>> = [];

  constructor(private nodeIds: string[], private params: ModifyTableGridAtIndexParams) {
    super('Modify Table Grid At Index', `${params.op} ${params.axis}`);
  }

  protected doExecute(): NodeChangeRecord[] {
    const records: NodeChangeRecord[] = [];
    const mutationManager = this.getMutationManager() as any;

    const batch: Array<{ nodeId: string; changes: any }> = [];
    const dockedDeleteIds = new Set<string>();
    const storeNodes = useDiagramStore.getState().nodes;
    this.deletedDockedNodes = [];

    for (const nodeId of this.nodeIds) {
      const node = mutationManager.getNode(nodeId) as any;
      if (!node) continue;
      if (node.type !== 'table') continue; // Guard: only tables
      const attrs = (node.attrs ?? {}) as Record<string, unknown>;

      const rowCount = clampCount(attrs.rowCount, 3);
      const colCount = clampCount(attrs.columnCount, 3);
      const showHeader = attrs.showHeader !== false;
      const headerHeight = typeof attrs.headerHeight === 'number' && Number.isFinite(attrs.headerHeight) && attrs.headerHeight > 0 ? attrs.headerHeight as number : 32;

      const width = (node.width ?? node.w ?? 240) as number;
      const height = (node.height ?? node.h ?? 180) as number;
      const bodyHeight = Math.max(MIN_CELL * rowCount, height - (showHeader ? headerHeight : 0));

      const rowHeights = ensureArray(attrs.rowHeights, rowCount, bodyHeight);
      const colWidths = ensureArray(attrs.columnWidths, colCount, width);

      const changes: any = { attrs: {} as Record<string, unknown> };

      if (this.params.axis === 'row') {
        const rRender = this.params.rowIndexByNodeId?.[nodeId];
        if (typeof rRender !== 'number' || !Number.isFinite(rRender)) continue;
        // Header row is index 0 when visible
        // Approved behavior:
        // - Insert "after" on header inserts first body row
        // - Insert "before" on header is not supported
        // - Remove on header is not supported
        const isHeaderRow = showHeader && rRender === 0;
        if (isHeaderRow && (this.params.op === 'remove' || this.params.position === 'before')) {
          continue;
        }

        const bodyIndex = isHeaderRow ? 0 : (showHeader ? rRender - 1 : rRender);
        if (bodyIndex < 0 || bodyIndex >= rowCount) continue;

        if (this.params.op === 'insert') {
          const insertAt = isHeaderRow ? 0 : ((this.params.position === 'before') ? bodyIndex : bodyIndex + 1);
          const base = rowHeights[Math.min(bodyIndex, rowHeights.length - 1)] ?? Math.max(MIN_CELL, bodyHeight / Math.max(1, rowCount));
          const newHeight = base;
          const newRowHeights = [...rowHeights];
          newRowHeights.splice(insertAt, 0, newHeight);

          // shift cell maps from render index R = insertAt (+ header offset)
          const fromRender = showHeader ? insertAt + 1 : insertAt;
          const oldText = ((attrs as any).cellText ?? {}) as Record<string, string>;
          const oldStyles = ((attrs as any).cellStyles ?? {}) as Record<string, unknown>;
          const oldLayouts = ((attrs as any).cellLayouts ?? {}) as Record<string, unknown>;
          const oldMerged = (attrs as any).mergedRegions;

          const nextText = shiftCells(oldText, 'row', fromRender, 1);
          let nextStyles = shiftCells(oldStyles, 'row', fromRender, 1);
          const nextLayouts = shiftCells(oldLayouts, 'row', fromRender, 1);
          
          // Inherit styles from the selected row to the new row
          // After shifting, if inserting "before", the selected row (rRender) is now at fromRender + 1
          // If inserting "after", the selected row (rRender) is still at fromRender (since fromRender = rRender + 1)
          const totalRows = showHeader ? rowCount + 1 : rowCount;
          // The source is the selected row after shifting: if inserting before, it's at fromRender + 1; if after, it's at fromRender - 1
          const sourceRenderIndex = this.params.position === 'before' 
            ? (fromRender + 1 < totalRows ? fromRender + 1 : fromRender - 1)
            : (fromRender > 0 ? fromRender - 1 : fromRender + 1);
          if (sourceRenderIndex >= 0 && sourceRenderIndex < totalRows) {
            nextStyles = inheritStylesFromNeighbor(nextStyles, 'row', fromRender, sourceRenderIndex, totalRows, colCount);
          }
          
          const nextMerged = adjustMergedRegionsOnInsert(oldMerged, 'row', fromRender);

          const nextBodyHeight = bodyHeight + newHeight;
          const nextTotalHeight = (showHeader ? headerHeight : 0) + nextBodyHeight;

          changes.attrs.rowCount = rowCount + 1;
          changes.attrs.rowHeights = newRowHeights;
          changes.attrs.cellText = nextText;
          changes.attrs.cellStyles = nextStyles;
          changes.attrs.cellLayouts = nextLayouts;
          changes.attrs.mergedRegions = nextMerged;
          changes.height = nextTotalHeight;
          changes.h = nextTotalHeight;

          SessionMetrics.increment(TABLE_SESSION_METRICS.ROWS_ADDED);
        } else if (this.params.op === 'remove') {
          if (rowCount <= 1) continue; // cannot remove last
          const removedHeight = rowHeights[bodyIndex] ?? MIN_CELL;
          const newRowHeights = rowHeights.filter((_, i) => i !== bodyIndex);
          const removeRender = showHeader ? bodyIndex + 1 : bodyIndex;
          for (const n of storeNodes as any[]) {
            const dock = n?.attrs?.tableDock;
            if (!dock || dock.tableId !== nodeId) continue;
            const parsed = parseCellKey(dock.cellKey);
            if (parsed && parsed.r === removeRender) dockedDeleteIds.add(n.id);
          }
          const oldText = ((attrs as any).cellText ?? {}) as Record<string, string>;
          const oldStyles = ((attrs as any).cellStyles ?? {}) as Record<string, unknown>;
          const oldLayouts = ((attrs as any).cellLayouts ?? {}) as Record<string, unknown>;
          const oldMerged = (attrs as any).mergedRegions;
          const nextText = removeCellsAtIndex(oldText, 'row', removeRender);
          const nextStyles = removeCellsAtIndex(oldStyles, 'row', removeRender);
          const nextLayouts = removeCellsAtIndex(oldLayouts, 'row', removeRender);
          const nextMerged = adjustMergedRegionsOnRemove(oldMerged, 'row', removeRender);

          const nextBodyHeight = Math.max(MIN_CELL * Math.max(1, rowCount - 1), bodyHeight - removedHeight);
          const nextTotalHeight = (showHeader ? headerHeight : 0) + nextBodyHeight;

          changes.attrs.rowCount = rowCount - 1;
          changes.attrs.rowHeights = newRowHeights;
          changes.attrs.cellText = nextText;
          changes.attrs.cellStyles = nextStyles;
          changes.attrs.cellLayouts = nextLayouts;
          changes.attrs.mergedRegions = nextMerged;
          changes.height = nextTotalHeight;
          changes.h = nextTotalHeight;

          SessionMetrics.increment(TABLE_SESSION_METRICS.ROWS_REMOVED);
        }
      } else if (this.params.axis === 'column') {
        const c = this.params.colIndexByNodeId?.[nodeId];
        if (typeof c !== 'number' || !Number.isFinite(c)) continue;
        if (c < 0 || c >= colCount) continue;

        if (this.params.op === 'insert') {
          const insertAt = (this.params.position === 'before') ? c : c + 1;
          const base = colWidths[Math.min(c, colWidths.length - 1)] ?? Math.max(MIN_CELL, width / Math.max(1, colCount));
          const newColWidths = [...colWidths];
          newColWidths.splice(insertAt, 0, base);

          const oldText = ((attrs as any).cellText ?? {}) as Record<string, string>;
          const oldStyles = ((attrs as any).cellStyles ?? {}) as Record<string, unknown>;
          const oldLayouts = ((attrs as any).cellLayouts ?? {}) as Record<string, unknown>;
          const oldMerged = (attrs as any).mergedRegions;
          const nextText = shiftCells(oldText, 'column', insertAt, 1);
          let nextStyles = shiftCells(oldStyles, 'column', insertAt, 1);
          const nextLayouts = shiftCells(oldLayouts, 'column', insertAt, 1);
          
          // Inherit styles from the selected column to the new column
          // After shifting, if inserting "before", the selected column (c) is now at insertAt + 1
          // If inserting "after", the selected column (c) is still at insertAt (since insertAt = c + 1)
          const totalRows = showHeader ? rowCount + 1 : rowCount;
          // The source is the selected column after shifting: if inserting before, it's at insertAt + 1; if after, it's at insertAt - 1
          const sourceColIndex = this.params.position === 'before'
            ? (insertAt + 1 < colCount + 1 ? insertAt + 1 : insertAt - 1)
            : (insertAt > 0 ? insertAt - 1 : insertAt + 1);
          if (sourceColIndex >= 0 && sourceColIndex < colCount + 1) {
            nextStyles = inheritStylesFromNeighbor(nextStyles, 'column', insertAt, sourceColIndex, totalRows, colCount + 1);
          }
          
          const nextMerged = adjustMergedRegionsOnInsert(oldMerged, 'column', insertAt);

          const nextWidth = (width + base);

          changes.attrs.columnCount = colCount + 1;
          changes.attrs.columnWidths = newColWidths;
          changes.attrs.cellText = nextText;
          changes.attrs.cellStyles = nextStyles;
          changes.attrs.cellLayouts = nextLayouts;
          changes.attrs.mergedRegions = nextMerged;
          changes.width = nextWidth;
          changes.w = nextWidth;

          SessionMetrics.increment(TABLE_SESSION_METRICS.COLS_ADDED);
        } else if (this.params.op === 'remove') {
          if (colCount <= 1) continue; // cannot remove last
          const removedWidth = colWidths[c] ?? MIN_CELL;
          const newColWidths = colWidths.filter((_, i) => i !== c);
          for (const n of storeNodes as any[]) {
            const dock = n?.attrs?.tableDock;
            if (!dock || dock.tableId !== nodeId) continue;
            const parsed = parseCellKey(dock.cellKey);
            if (parsed && parsed.c === c) dockedDeleteIds.add(n.id);
          }

          const oldText = ((attrs as any).cellText ?? {}) as Record<string, string>;
          const oldStyles = ((attrs as any).cellStyles ?? {}) as Record<string, unknown>;
          const oldLayouts = ((attrs as any).cellLayouts ?? {}) as Record<string, unknown>;
          const oldMerged = (attrs as any).mergedRegions;
          const nextText = removeCellsAtIndex(oldText, 'column', c);
          const nextStyles = removeCellsAtIndex(oldStyles, 'column', c);
          const nextLayouts = removeCellsAtIndex(oldLayouts, 'column', c);
          const nextMerged = adjustMergedRegionsOnRemove(oldMerged, 'column', c);

          const nextWidth = Math.max(MIN_CELL * Math.max(1, colCount - 1), width - removedWidth);

          changes.attrs.columnCount = colCount - 1;
          changes.attrs.columnWidths = newColWidths;
          changes.attrs.cellText = nextText;
          changes.attrs.cellStyles = nextStyles;
          changes.attrs.cellLayouts = nextLayouts;
          changes.attrs.mergedRegions = nextMerged;
          changes.width = nextWidth;
          changes.w = nextWidth;

          SessionMetrics.increment(TABLE_SESSION_METRICS.COLS_REMOVED);
        }
      }

      if (Object.keys(changes.attrs).length === 0) continue;
      const latest = mutationManager.getNode(nodeId);
      changes.x = (latest as any)?.x ?? node.x ?? 0;
      changes.y = (latest as any)?.y ?? node.y ?? 0;

      batch.push({ nodeId, changes });

      const previewAttrs = { ...(attrs as any), ...(changes.attrs as any) };
      const preview: any = { ...node, ...changes, attrs: previewAttrs };
      
      // Update docked node cellKeys when inserting rows/columns
      const dockedToTable = storeNodes.filter((n) => {
        const dock = (n as any)?.attrs?.tableDock;
        return !!dock && dock.tableId === nodeId;
      });
      
      // Calculate new cellKeys for docked nodes
      const cellKeyUpdates = new Map<string, `${number},${number}`>();
      
      if (this.params.op === 'insert') {
        for (const n of dockedToTable) {
          const dock = (n as any)?.attrs?.tableDock;
          if (!dock) continue;
          const parsed = parseCellKey(dock.cellKey);
          if (!parsed) continue;
          
          let newR = parsed.r;
          let newC = parsed.c;
          
          if (this.params.axis === 'row') {
            const rRender = this.params.rowIndexByNodeId?.[nodeId];
            if (typeof rRender === 'number' && Number.isFinite(rRender)) {
              const isHeaderRow = showHeader && rRender === 0;
              const bodyIndex = isHeaderRow ? 0 : (showHeader ? rRender - 1 : rRender);
              const insertAt = isHeaderRow ? 0 : ((this.params.position === 'before') ? bodyIndex : bodyIndex + 1);
              const fromRender = showHeader ? insertAt + 1 : insertAt;
              if (parsed.r >= fromRender) {
                newR = parsed.r + 1;
              }
            }
          } else if (this.params.axis === 'column') {
            const c = this.params.colIndexByNodeId?.[nodeId];
            if (typeof c === 'number' && Number.isFinite(c)) {
              const insertAt = (this.params.position === 'before') ? c : c + 1;
              if (parsed.c >= insertAt) {
                newC = parsed.c + 1;
              }
            }
          }
          
          if (newR !== parsed.r || newC !== parsed.c) {
            const newCellKey = makeCellKey(newR, newC);
            cellKeyUpdates.set(n.id, newCellKey);
            const nextDock = { ...dock, cellKey: newCellKey };
            batch.push({
              nodeId: n.id,
              changes: {
                attrs: {
                  tableDock: nextDock,
                },
              },
            });
          }
        }
      }
      
      // Create preview nodes with updated cellKeys for layout computation
      const previewNodes = storeNodes.map((n) => {
        const newCellKey = cellKeyUpdates.get(n.id);
        if (!newCellKey) return n;
        const dock = (n as any)?.attrs?.tableDock;
        if (!dock || dock.tableId !== nodeId) return n;
        const nextDock = { ...dock, cellKey: newCellKey };
        return { ...n, attrs: { ...(n as any).attrs, tableDock: nextDock } } as any;
      });
      
      const dockedUpdates = computeDockedLayoutUpdatesForTable(preview, previewNodes);
      dockedUpdates.filter((u) => !dockedDeleteIds.has(u.nodeId)).forEach((u) => {
        // Merge with existing batch entry if present
        const existing = batch.find((b) => b.nodeId === u.nodeId);
        if (existing) {
          existing.changes = {
            ...existing.changes,
            ...u.changes,
            attrs: {
              ...(existing.changes.attrs ?? {}),
              ...(u.changes.attrs ?? {}),
            },
          };
        } else {
          batch.push(u);
        }
      });
    }

    if (batch.length > 1 && typeof (mutationManager as any).applyBatchNodeChanges === 'function') {
      const recs = (mutationManager as any).applyBatchNodeChanges(batch);
      records.push(...recs);
    } else if (batch.length === 1) {
      const { nodeId, changes } = batch[0];
      const rec = (mutationManager as any).applyNodeChange(nodeId, changes);
      if (rec) records.push(rec);
    }

    if (dockedDeleteIds.size > 0) {
      const toDelete = Array.from(dockedDeleteIds);
      const snapshot = storeNodes.filter((n) => dockedDeleteIds.has(n.id));
      this.deletedDockedNodes = snapshot.map((n) => ({ ...(n as any) }));
      if (toDelete.length > 1 && typeof (mutationManager as any).batchDeleteNodes === 'function') {
        (mutationManager as any).batchDeleteNodes(toDelete);
      } else {
        toDelete.forEach((id) => {
          (mutationManager as any).deleteNode(id);
        });
      }
    }

    return records;
  }

  undo(): void {
    const mutationManager = this.getMutationManager() as any;
    [...this.changeRecords].reverse().forEach(record =>
      mutationManager.revertNodeChange(record)
    );

    if (this.deletedDockedNodes.length > 0) {
      if (typeof (mutationManager as any).batchAddNodes === 'function') {
        (mutationManager as any).batchAddNodes(this.deletedDockedNodes as any);
      } else if (typeof (mutationManager as any).addNode === 'function') {
        this.deletedDockedNodes.forEach((node) => (mutationManager as any).addNode(node));
      }
    }
  }
}
